import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Database, Send, CheckCircle2, AlertCircle, Loader2, RefreshCw, Trash2, ZapOff } from "lucide-react";
import api from "../../src/api";
import { toast } from "sonner";

export function SimulationView() {
    const [folio, setFolio] = useState("");
    const [monto, setMonto] = useState("500");
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
    const [oracleStatus, setOracleStatus] = useState<any>(null);
    const [resetFolio, setResetFolio] = useState("");
    const [isResetting, setIsResetting] = useState(false);

    const checkStatus = async () => {
        setStatus("loading");
        try {
            const { data } = await api.get("/simulation/status");
            setOracleStatus(data);
            setStatus(data?.online ? "ready" : "error");
        } catch (error) {
            console.error("Oracle status check failed", error);
            setOracleStatus({ online: false, message: "Oracle no disponible" });
            setStatus("error");
        }
    };

    useEffect(() => {
        checkStatus();
    }, []);

    const handleInject = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!folio) {
            toast.error("El folio es requerido");
            return;
        }

        setIsLoading(true);
        try {
            const { data } = await api.post("/simulation/inject-payment", {
                folio: folio.trim(),
                monto: Number(monto)
            });
            toast.success(data.message || "Pago inyectado exitosamente.");
            setFolio("");
        } catch (error: any) {
            const isConflict = error.response?.status === 409;
            const msg = error.response?.data?.message || "Error al inyectar pago";

            if (isConflict) {
                const confirmReset = confirm(
                    `${msg}\n\n¿Deseas LIMPIAR EL HISTORIAL Y RE-INYECTAR automáticamente para probar de nuevo?`
                );

                if (confirmReset) {
                    setIsLoading(true);
                    try {
                        const { data: forceData } = await api.post("/simulation/inject-payment", {
                            folio: folio.trim(),
                            monto: Number(monto),
                            force: true
                        });
                        toast.success(forceData.message || "Ciclo recuperado e inyectado.");
                        setFolio("");
                    } catch (forceErr: any) {
                        toast.error("Error al intentar la recuperación forzada");
                    } finally {
                        setIsLoading(false);
                    }
                }
            } else {
                toast.error(msg);
            }
        } finally {
            setIsLoading(false);
        }
    };
    const handleDeepReset = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!resetFolio) {
            toast.error("El folio es requerido para el reseteo profundo");
            return;
        }

        if (!confirm(`¿Estás SEGURO de querer eliminar COMPLETAMENTE el registro ${resetFolio}? Esta acción borrará los datos en Prisma y Oracle. No se puede deshacer.`)) {
            return;
        }

        setIsResetting(true);
        try {
            const { data } = await api.delete("/simulation/reset-full-cycle", {
                data: { folio: resetFolio.trim() }
            });
            toast.success(data.message || "Ciclo reseteado correctamente");
            if (Array.isArray(data?.warnings) && data.warnings.length > 0) {
                toast.warning(data.warnings.join(" | "));
            }
            setResetFolio("");
        } catch (error: any) {
            const msg = error.response?.data?.message || "Error al resetear el ciclo";
            toast.error(msg);
        } finally {
            setIsResetting(false);
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in-50 duration-500">
            <div className="flex flex-col gap-2">
                <h2 className="text-2xl font-bold text-[#0f2744]">Simulador de Ciclo de Vida</h2>
                <p className="text-gray-500">
                    Control absoluto para SuperAdmin: Simula flujos de pago o resetea ciclos completos para pruebas iterativas.
                </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
                {/* Formulario de Inyección */}
                <Card className="shadow-sm border-gray-200">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Send className="h-5 w-5 text-[#1e3a5f]" />
                            Inyectar Pago Confirmado
                        </CardTitle>
                        <CardDescription>
                            Simula que tesorería ha confirmado el pago de un folio específico.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleInject} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="folio">Folio del Registro</Label>
                                <Input
                                    id="folio"
                                    placeholder="ej. CEU-2026-XXXX"
                                    value={folio}
                                    onChange={(e) => setFolio(e.target.value)}
                                    className="border-gray-300 focus:ring-[#0f2744]"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="monto">Monto Simulado ($)</Label>
                                <Input
                                    id="monto"
                                    type="number"
                                    value={monto}
                                    onChange={(e) => setMonto(e.target.value)}
                                    className="border-gray-300 focus:ring-[#0f2744]"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-[#0f2744] hover:bg-[#1a3a5a] text-white"
                                disabled={isLoading || status !== "ready"}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Inyectando...
                                    </>
                                ) : (
                                    "Disparar Conciliación"
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {/* Estado de Infraestructura */}
                <Card className="shadow-sm border-gray-200">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-lg">
                            <Database className="h-5 w-5 text-[#c9a227]" />
                            Infraestructura de Simulación
                        </CardTitle>
                        <CardDescription>
                            Estado de la conexión con el entorno local de Oracle.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="flex items-center justify-between p-4 rounded-lg bg-gray-50 border border-gray-100">
                            <div className="flex items-center gap-3">
                                <div className={`h-3 w-3 rounded-full ${status === 'ready' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]' : status === 'loading' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                                <div>
                                    <p className="text-sm font-bold text-[#0f2744]">Oracle XE Local</p>
                                    <p className="text-xs text-gray-500">{status === 'ready' ? 'Conexión Establecida' : status === 'loading' ? 'Verificando...' : 'Error de Conexión'}</p>
                                </div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={checkStatus} disabled={status === 'loading'}>
                                <RefreshCw className={`h-4 w-4 text-gray-400 ${status === 'loading' ? 'animate-spin' : ''}`} />
                            </Button>
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                                <span>Worker de conciliación: <b>Activo (1 min)</b></span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                {oracleStatus?.online ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                ) : (
                                    <AlertCircle className="h-4 w-4 text-red-500" />
                                )}
                                <span>Base de Datos Oracle: <b className={oracleStatus?.online ? "text-green-600" : "text-red-600"}>
                                    {oracleStatus?.online ? 'Online' : 'Offline'}
                                </b></span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                {oracleStatus?.online ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                ) : (
                                    <AlertCircle className="h-4 w-4 text-blue-500" />
                                )}
                                <span>Tabla: <b>FINANZAS_PAYMENTS</b> {oracleStatus?.online ? 'lista' : 'no disponible'}</span>
                            </div>
                        </div>

                        <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                            <p className="text-xs text-blue-800 leading-relaxed">
                                <b>Tip:</b> Primero crea un registro en el portal público, copia el folio, y luego úsalo aquí. El registro pasará de "Pendiente" a "Pagado" y recibirás el QR por correo automáticamente en menos de un minuto.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* RESET PROFUNDO (Solo SuperAdmin) */}
            <Card className="shadow-sm border-red-100 bg-red-50/10">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg text-red-800">
                        <ZapOff className="h-5 w-5" />
                        Reset de Ciclo Completo (Deep Reset)
                    </CardTitle>
                    <CardDescription className="text-red-600/80">
                        <b>ZONA DE PELIGRO:</b> Borra permanentemente un registro de Prisma y su rastro en Oracle. Permite volver a registrar el mismo folio/correo desde cero.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleDeepReset} className="flex flex-col md:flex-row gap-4 items-end">
                        <div className="flex-grow space-y-2">
                            <Label htmlFor="resetFolio" className="text-red-900">Folio a Eliminar</Label>
                            <Input
                                id="resetFolio"
                                placeholder="ej. CIU-2026-XXXX-XXXX"
                                value={resetFolio}
                                onChange={(e) => setResetFolio(e.target.value)}
                                className="border-red-200 focus:ring-red-500"
                            />
                        </div>
                        <Button
                            type="submit"
                            variant="destructive"
                            className="bg-red-600 hover:bg-red-700 h-10 px-8"
                            disabled={isResetting}
                        >
                            {isResetting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Borrando...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Limpiar Registro y Oracle
                                </>
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
